import api from "@/services/api";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import type { SelectOption } from "@/components/ui/Select";
import { formatCpfCnpjInput, onlyDigits } from "@/utils/brDocument";

/** Limite do Sienge para número do documento (campo Descrição do pagamento). */
export const DESCRICAO_PAGAMENTO_MAX_LENGTH = 20;

/** Limite do campo Observação na solicitação de pagamento. */
export const OBSERVACAO_MAX_LENGTH = 500;

export type FormaPagamento = "BOLETO" | "PIX" | "TRANSFERENCIA";
export type PixTipoChave = "CELULAR" | "CPF_CNPJ" | "EMAIL" | "ALEATORIA";
export type SolicitacaoPagamentoStatus =
  | "pendente_aprovacao"
  | "pendente_financeira"
  | "aguardando_sienge"
  | "aprovada"
  | "reprovada"
  | "cancelada";

export type SolicitacaoTimelineEventType =
  | "criada"
  | "aprovada_operacional"
  | "reprovada_operacional"
  | "aprovada_financeira"
  | "reprovada_financeira"
  | "titulo_sienge_preview"
  | "titulo_sienge_inserido"
  | "reenviada"
  | "cancelada";

export interface SolicitacaoTimelineEvent {
  id: string;
  type: SolicitacaoTimelineEventType;
  at: string;
  userId: string;
  userName: string;
  note?: string;
}

export interface FormUserOption {
  id: string;
  displayName: string;
  username: string;
}

export interface SolicitacaoPagamentoDto {
  id: string;
  status: SolicitacaoPagamentoStatus;
  solicitanteId: string;
  solicitanteName: string;
  aprovadorId: string;
  aprovadorName: string;
  costCenterId: string;
  costCenterLabel: string;
  paymentCategoryId: string;
  paymentCategoryLabel: string;
  creditorId: string;
  creditorName: string;
  creditorDocument: string;
  descricao: string;
  valor: number;
  observacao: string;
  formaPagamento: FormaPagamento;
  boletoCodigoBarras: string | null;
  pixTipoChave: PixTipoChave | null;
  pixChave: string | null;
  pixContaEmNome: string | null;
  transferenciaTitular: string | null;
  transferenciaBanco: string | null;
  transferenciaAgencia: string | null;
  transferenciaConta: string | null;
  dueDate: string;
  documentIdentificationId: string;
  anexoStoredFile: string | null;
  anexoOriginalName: string | null;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
  reviewedById: string | null;
  reviewedByName: string | null;
  reviewNote: string | null;
  financeiroReviewedAt: string | null;
  financeiroReviewedById: string | null;
  financeiroReviewedByName: string | null;
  financeiroReviewNote: string | null;
  siengeBillPayload: Record<string, unknown> | null;
  siengeBillId: string | null;
  canceladaAt: string | null;
  canceladaById: string | null;
  canceladaByName: string | null;
  cancelNote: string | null;
  timeline: SolicitacaoTimelineEvent[];
}

export interface SiengeBillPreviewField {
  label: string;
  value: string;
}

export interface SiengeBillPreview {
  payload: Record<string, unknown>;
  fields: SiengeBillPreviewField[];
  missingFields: string[];
}

export const SOLICITACAO_DOCUMENT_ID = "REC ";

export interface SolicitacaoPagamentoFormOptions {
  solicitante: FormUserOption | null;
  aprovadores: FormUserOption[];
  formasPagamento: SelectOption[];
}

export interface CreditorOption {
  id: string;
  name: string;
  documentNumber: string;
}

export interface SolicitacaoPagamentoFormValues {
  solicitanteId: string;
  aprovadorId: string;
  costCenterId: string;
  costCenterLabel: string;
  paymentCategoryId: string;
  paymentCategoryLabel: string;
  creditorId: string;
  creditorName: string;
  creditorDocument: string;
  descricao: string;
  valor: string;
  observacao: string;
  formaPagamento: FormaPagamento | "";
  boletoCodigoBarras: string;
  pixTipoChave: PixTipoChave | "";
  pixChave: string;
  pixContaEmNome: string;
  transferenciaTitular: string;
  transferenciaBanco: string;
  transferenciaAgencia: string;
  transferenciaConta: string;
  dueDate: string;
  documentIdentificationId: string;
  anexoFile: File | null;
}

export const EMPTY_SOLICITACAO_FORM: SolicitacaoPagamentoFormValues = {
  solicitanteId: "",
  aprovadorId: "",
  costCenterId: "",
  costCenterLabel: "",
  paymentCategoryId: "",
  paymentCategoryLabel: "",
  creditorId: "",
  creditorName: "",
  creditorDocument: "",
  descricao: "",
  valor: "",
  observacao: "",
  formaPagamento: "",
  boletoCodigoBarras: "",
  pixTipoChave: "",
  pixChave: "",
  pixContaEmNome: "",
  transferenciaTitular: "",
  transferenciaBanco: "",
  transferenciaAgencia: "",
  transferenciaConta: "",
  dueDate: "",
  documentIdentificationId: SOLICITACAO_DOCUMENT_ID,
  anexoFile: null,
};

export function dtoToFormValues(item: SolicitacaoPagamentoDto): SolicitacaoPagamentoFormValues {
  return {
    solicitanteId: item.solicitanteId,
    aprovadorId: item.aprovadorId,
    costCenterId: item.costCenterId,
    costCenterLabel: item.costCenterLabel,
    paymentCategoryId: item.paymentCategoryId,
    paymentCategoryLabel: item.paymentCategoryLabel,
    creditorId: item.creditorId,
    creditorName: item.creditorName,
    creditorDocument: item.creditorDocument,
    descricao: item.descricao,
    valor: item.valor.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    observacao: item.observacao,
    formaPagamento: item.formaPagamento,
    boletoCodigoBarras: item.boletoCodigoBarras ?? "",
    pixTipoChave: item.pixTipoChave ?? "",
    pixChave: item.pixChave ?? "",
    pixContaEmNome: item.pixContaEmNome ?? "",
    transferenciaTitular: item.transferenciaTitular ?? "",
    transferenciaBanco: item.transferenciaBanco ?? "",
    transferenciaAgencia: item.transferenciaAgencia ?? "",
    transferenciaConta: item.transferenciaConta ?? "",
    dueDate: item.dueDate?.slice(0, 10) ?? "",
    documentIdentificationId: SOLICITACAO_DOCUMENT_ID,
    anexoFile: null,
  };
}

/** Espelha o backend (`extractCreditorRegisterNumber`) — Sienge usa `cpf`/`cnpj`. */
export function extractCreditorDocumentNumber(raw: Record<string, unknown>): string {
  for (const key of [
    "registerNumber",
    "documentNumber",
    "cpfCnpj",
    "taxId",
    "cpf",
    "cnpj",
  ]) {
    const v = raw[key];
    if (v != null && String(v).trim() !== "") {
      const digits = onlyDigits(String(v));
      return digits ? formatCpfCnpjInput(digits) : String(v).trim();
    }
  }
  return "";
}

function normalizeCreditorLabel(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/** Nome exibido: Nome Legal - Nome Fantasia (quando diferentes). */
export function creditorDisplayName(raw: Record<string, unknown>): string {
  const legal = raw.name != null ? String(raw.name).trim() : "";
  const trade = raw.tradeName != null ? String(raw.tradeName).trim() : "";
  if (!legal) return trade;
  if (!trade || normalizeCreditorLabel(legal) === normalizeCreditorLabel(trade)) return legal;
  return `${legal} - ${trade}`;
}

function extractList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (data != null && typeof data === "object") {
    const o = data as Record<string, unknown>;
    for (const key of ["data", "results", "content", "items"]) {
      if (Array.isArray(o[key])) return o[key] as Record<string, unknown>[];
    }
  }
  return [];
}

export async function fetchSolicitacaoPagamentoFormOptions(): Promise<SolicitacaoPagamentoFormOptions> {
  const { data } = await api.get<{
    success: boolean;
    solicitante: FormUserOption | null;
    aprovadores: FormUserOption[];
    formasPagamento: SelectOption[];
  }>("/api/solicitacao-pagamento/form-options");
  return {
    solicitante: data.solicitante ?? null,
    aprovadores: data.aprovadores ?? [],
    formasPagamento: data.formasPagamento ?? [],
  };
}

export async function fetchCreditorOptions(): Promise<CreditorOption[]> {
  const { data } = await api.get<{ data?: unknown }>("/api/creditors");
  const list = extractList(data);
  return list
    .map((raw) => {
      const id = raw.id != null ? String(raw.id).trim() : "";
      const name = creditorDisplayName(raw);
      const documentNumber = extractCreditorDocumentNumber(raw);
      return { id, name, documentNumber };
    })
    .filter((c) => c.id && c.name)
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
}

export interface CreditorCityOption {
  id: string;
  name: string;
  state: string;
  label: string;
}

export interface CreateCreditorAddressPayload {
  cityId: string;
  streetName: string;
  number: string;
  neighborhood: string;
  zipCode: string;
  complement?: string;
}

export interface CreateCreditorPayload {
  name: string;
  registerNumber: string;
  phoneDdd: string;
  phoneNumber: string;
  email?: string;
  tradeName?: string;
  stateRegistrationType?: "C" | "I" | "N";
  stateRegistrationNumber?: string;
  cityRegistrationNumber?: string;
  address: CreateCreditorAddressPayload;
}

export interface CnpjLookupResult {
  razaoSocial: string;
  nomeFantasia: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  municipio: string;
  uf: string;
  municipioIbge: string;
  stateRegistrationNumber: string;
  stateRegistrationType: "C" | "I" | "N";
  cityRegistrationNumber: string;
}

export async function fetchCreditorCityOptions(): Promise<CreditorCityOption[]> {
  const { data } = await api.get<{ success: boolean; cities: CreditorCityOption[] }>(
    "/api/creditors/cities",
  );
  return data.cities ?? [];
}

export async function checkCreditorDocument(registerNumber: string): Promise<{
  exists: boolean;
  creditor: CreditorOption | null;
}> {
  const { data } = await api.get<{
    success: boolean;
    exists: boolean;
    creditor: CreditorOption | null;
  }>("/api/creditors/check", {
    params: { registerNumber },
  });
  return {
    exists: data.exists ?? false,
    creditor: data.creditor ?? null,
  };
}

export async function lookupCreditorCnpj(cnpj: string): Promise<CnpjLookupResult | null> {
  const { data } = await api.get<{ success: boolean; data?: CnpjLookupResult }>(
    "/api/creditors/cnpj-lookup",
    { params: { cnpj } },
  );
  return data.data ?? null;
}

export async function createCreditor(payload: CreateCreditorPayload): Promise<CreditorOption> {
  const { data } = await api.post<{ success: boolean; creditor: CreditorOption }>(
    "/api/creditors",
    payload,
  );
  return data.creditor;
}

function isPaymentCategoryTotalizadora(raw: Record<string, unknown>): boolean {
  const tp = String(raw.tpConta ?? raw.type ?? "").trim().toUpperCase();
  return tp === "T" || tp === "TOTALIZADORA";
}

export async function fetchPaymentCategoryOptions(): Promise<SearchableSelectOption[]> {
  const { data } = await api.get<{ data?: unknown }>("/api/payment-categories");
  const list = extractList(data);
  return list
    .map((raw) => {
      const id = raw.id != null ? String(raw.id).trim() : "";
      const name = raw.name != null ? String(raw.name).trim() : "";
      const code =
        (raw.code != null ? String(raw.code).trim() : "") ||
        (raw.codigo != null ? String(raw.codigo).trim() : "") ||
        id;
      const label = code && name ? `${code} – ${name}` : name || id;
      const totalizadora = isPaymentCategoryTotalizadora(raw);
      return {
        value: id,
        label,
        disabled: totalizadora,
        labelBold: totalizadora,
      };
    })
    .filter((o) => o.value)
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
}

export async function listSolicitacoesPagamento(): Promise<{
  manageAll: boolean;
  todas: SolicitacaoPagamentoDto[];
  minhas: SolicitacaoPagamentoDto[];
  pendentesOperacional: SolicitacaoPagamentoDto[];
  pendentesFinanceira: SolicitacaoPagamentoDto[];
  aguardandoSienge: SolicitacaoPagamentoDto[];
}> {
  const { data } = await api.get<{
    success: boolean;
    manageAll?: boolean;
    todas?: SolicitacaoPagamentoDto[];
    minhas: SolicitacaoPagamentoDto[];
    pendentesOperacional: SolicitacaoPagamentoDto[];
    pendentesFinanceira: SolicitacaoPagamentoDto[];
    aguardandoSienge?: SolicitacaoPagamentoDto[];
    pendentes?: SolicitacaoPagamentoDto[];
  }>("/api/solicitacao-pagamento");
  const todas = data.todas ?? data.minhas ?? [];
  return {
    manageAll: data.manageAll === true,
    todas,
    minhas: data.minhas ?? todas,
    pendentesOperacional: data.pendentesOperacional ?? data.pendentes ?? [],
    pendentesFinanceira: data.pendentesFinanceira ?? [],
    aguardandoSienge: data.aguardandoSienge ?? [],
  };
}

function appendDadosPagamento(fd: FormData, values: SolicitacaoPagamentoFormValues): void {
  fd.append("boletoCodigoBarras", values.boletoCodigoBarras);
  fd.append("pixTipoChave", values.pixTipoChave);
  fd.append("pixChave", values.pixChave);
  fd.append("pixContaEmNome", values.pixContaEmNome);
  fd.append("transferenciaTitular", values.transferenciaTitular);
  fd.append("transferenciaBanco", values.transferenciaBanco);
  fd.append("transferenciaAgencia", values.transferenciaAgencia);
  fd.append("transferenciaConta", values.transferenciaConta);
}

export async function createSolicitacaoPagamento(
  values: SolicitacaoPagamentoFormValues,
): Promise<SolicitacaoPagamentoDto> {
  const fd = new FormData();
  fd.append("aprovadorId", values.aprovadorId);
  fd.append("costCenterId", values.costCenterId);
  fd.append("costCenterLabel", values.costCenterLabel);
  fd.append("paymentCategoryId", values.paymentCategoryId);
  fd.append("paymentCategoryLabel", values.paymentCategoryLabel);
  fd.append("creditorId", values.creditorId);
  fd.append("creditorName", values.creditorName);
  fd.append("creditorDocument", values.creditorDocument);
  fd.append("descricao", values.descricao);
  fd.append("valor", values.valor);
  fd.append("observacao", values.observacao);
  fd.append("formaPagamento", values.formaPagamento);
  fd.append("dueDate", values.dueDate);
  fd.append("documentIdentificationId", SOLICITACAO_DOCUMENT_ID);
  appendDadosPagamento(fd, values);
  if (values.anexoFile) fd.append("anexo", values.anexoFile);

  const { data } = await api.post<{ success: boolean; request: SolicitacaoPagamentoDto }>(
    "/api/solicitacao-pagamento",
    fd,
  );
  return data.request;
}

export async function updateSolicitacaoPagamento(
  id: string,
  values: SolicitacaoPagamentoFormValues,
): Promise<SolicitacaoPagamentoDto> {
  const fd = new FormData();
  fd.append("aprovadorId", values.aprovadorId);
  fd.append("costCenterId", values.costCenterId);
  fd.append("costCenterLabel", values.costCenterLabel);
  fd.append("paymentCategoryId", values.paymentCategoryId);
  fd.append("paymentCategoryLabel", values.paymentCategoryLabel);
  fd.append("creditorId", values.creditorId);
  fd.append("creditorName", values.creditorName);
  fd.append("creditorDocument", values.creditorDocument);
  fd.append("descricao", values.descricao);
  fd.append("valor", values.valor);
  fd.append("observacao", values.observacao);
  fd.append("formaPagamento", values.formaPagamento);
  fd.append("dueDate", values.dueDate);
  fd.append("documentIdentificationId", SOLICITACAO_DOCUMENT_ID);
  appendDadosPagamento(fd, values);
  if (values.anexoFile) fd.append("anexo", values.anexoFile);

  const { data } = await api.patch<{ success: boolean; request: SolicitacaoPagamentoDto }>(
    `/api/solicitacao-pagamento/${id}`,
    fd,
  );
  return data.request;
}

export async function finalizarTituloSienge(
  id: string,
): Promise<{
  request: SolicitacaoPagamentoDto;
  siengeBillId: string;
  anexoSiengeEnviado?: boolean;
  pdfSiengeEnviado?: boolean;
}> {
  const { data } = await api.post<{
    success: boolean;
    request: SolicitacaoPagamentoDto;
    siengeBillId: string;
    anexoSiengeEnviado?: boolean;
    anexoSiengeAviso?: string | null;
    pdfSiengeEnviado?: boolean;
    error?: string;
  }>(`/api/solicitacao-pagamento/${id}/finalizar-sienge`);
  if (!data.success) {
    throw new Error(data.error ?? "Erro ao inserir titulo no Sienge.");
  }
  return {
    request: data.request,
    siengeBillId: data.siengeBillId,
    anexoSiengeEnviado: data.anexoSiengeEnviado,
    pdfSiengeEnviado: data.pdfSiengeEnviado,
  };
}

export async function aprovarSolicitacaoOperacional(id: string, reviewNote?: string): Promise<void> {
  await api.patch(`/api/solicitacao-pagamento/${id}/aprovar-operacional`, {
    reviewNote: reviewNote ?? "",
  });
}

export async function reprovarSolicitacaoOperacional(id: string, reviewNote: string): Promise<void> {
  await api.patch(`/api/solicitacao-pagamento/${id}/reprovar-operacional`, { reviewNote });
}

export async function aprovarSolicitacaoFinanceira(
  id: string,
  reviewNote?: string,
): Promise<{ request: SolicitacaoPagamentoDto; billPreview: SiengeBillPreview }> {
  const { data } = await api.patch<{
    success: boolean;
    request: SolicitacaoPagamentoDto;
    billPreview: SiengeBillPreview;
  }>(`/api/solicitacao-pagamento/${id}/aprovar-financeira`, { reviewNote: reviewNote ?? "" });
  return { request: data.request, billPreview: data.billPreview };
}

export async function reprovarSolicitacaoFinanceira(id: string, reviewNote: string): Promise<void> {
  await api.patch(`/api/solicitacao-pagamento/${id}/reprovar-financeira`, { reviewNote });
}

export async function cancelarSolicitacaoPagamento(
  id: string,
  cancelNote: string,
): Promise<SolicitacaoPagamentoDto> {
  const { data } = await api.patch<{ success: boolean; request: SolicitacaoPagamentoDto }>(
    `/api/solicitacao-pagamento/${id}/cancelar`,
    { cancelNote },
  );
  return data.request;
}

export async function listSolicitacoesArquivadas(): Promise<SolicitacaoPagamentoDto[]> {
  const { data } = await api.get<{ success: boolean; arquivadas: SolicitacaoPagamentoDto[] }>(
    "/api/solicitacao-pagamento/arquivadas",
  );
  return data.arquivadas ?? [];
}

export function canUserCancelSolicitacao(
  item: SolicitacaoPagamentoDto,
  userId: string,
  opts: {
    isAdmin: boolean;
    canAprovarOperacional: boolean;
    canAprovarFinanceira: boolean;
  },
): boolean {
  if (item.status === "aprovada" || item.status === "cancelada") return false;
  if (!userId) return false;
  if (opts.isAdmin) return true;
  if (item.solicitanteId === userId) return true;
  if (opts.canAprovarOperacional && item.aprovadorId === userId) return true;
  if (opts.canAprovarFinanceira) {
    if (item.status === "pendente_financeira" || item.status === "aguardando_sienge") return true;
    if (item.financeiroReviewedById === userId) return true;
  }
  return false;
}

/** @deprecated Use aprovarSolicitacaoOperacional */
export async function aprovarSolicitacaoPagamento(id: string, reviewNote?: string): Promise<void> {
  await aprovarSolicitacaoOperacional(id, reviewNote);
}

/** @deprecated Use reprovarSolicitacaoOperacional */
export async function reprovarSolicitacaoPagamento(id: string, reviewNote: string): Promise<void> {
  await reprovarSolicitacaoOperacional(id, reviewNote);
}

export async function fetchTituloSiengePreview(id: string): Promise<SiengeBillPreview> {
  const { data } = await api.get<{ success: boolean; preview: SiengeBillPreview }>(
    `/api/solicitacao-pagamento/${id}/titulo-sienge-preview`,
  );
  return data.preview;
}

export function solicitacaoAnexoUrl(id: string): string {
  return `/api/solicitacao-pagamento/${id}/anexo`;
}

export const TIMELINE_EVENT_LABELS: Record<SolicitacaoTimelineEventType, string> = {
  criada: "Solicitação criada",
  aprovada_operacional: "Aprovada (operacional)",
  reprovada_operacional: "Reprovada (operacional)",
  aprovada_financeira: "Aprovada (financeira)",
  reprovada_financeira: "Reprovada (financeira)",
  titulo_sienge_preview: "Preview do título Sienge gerado",
  titulo_sienge_inserido: "Título inserido no Sienge",
  reenviada: "Reenviada para aprovação",
  cancelada: "Solicitação cancelada",
};

export const STATUS_LABELS: Record<SolicitacaoPagamentoStatus, string> = {
  pendente_aprovacao: "Aguardando aprovação operacional",
  pendente_financeira: "Aguardando aprovação financeira",
  aguardando_sienge: "Aguardando envio ao Sienge",
  aprovada: "Aprovada",
  reprovada: "Reprovada — editável",
  cancelada: "Cancelada",
};
